import { type DateRange, type EntityFilter, parseDateString, toDateString } from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import type { LitElement } from "lit";
import { property } from "lit/decorators.js";
import { app, appMode, onAppDispose, router } from "../init";
import { setSuggestedHelpPage } from "../lib/help-page";
import { persistent } from "../lib/hmr-persistent";

type Constructor<T> = new (...args: any[]) => T;

interface RoutePropertyDeclaration {
    name: string;
    arg?: string | number;
    param?: string | number;
    type?: StringConstructor | NumberConstructor | DateConstructor | BooleanConstructor | ObjectConstructor;
    default?: () => Exclude<any, undefined>;
    parse?: (inp: string) => string | number;
}

export type RouteArgs = Array<string | number | Date | boolean> & Record<string, string | number | Date | boolean>;

export function routeProperty(options: Omit<RoutePropertyDeclaration, "name">) {
    return (proto: object & { routeProperties: RoutePropertyDeclaration[] }, name: string) => {
        if (!proto.routeProperties) {
            proto.routeProperties = [];
        }
        proto.routeProperties.push({ ...options, name });
        return property(options)(proto, name);
    };
}

function makeReadyState() {
    let resolve!: (val: boolean) => void;
    let reject!: (err: unknown) => void;
    const promise = new Promise<boolean>((res, rej) => {
        resolve = res;
        reject = rej;
    });
    return { promise, resolve, reject };
}

// Persist promise + resolver together: a bare module-level promise would be
// rebuilt (left forever-pending) every time HMR re-executes this module, so any
// later `await isReady` would hang.
let readyState = persistent(Symbol.for("routing.ready"), makeReadyState);

export let isReady = readyState.promise;

export function ready() {
    readyState.resolve(true);
}

/**
 * Marks the app boot as failed: `isReady` rejects, so the integration wrapper
 * can surface the failure as `pentacode-error` instead of hanging forever
 * (PTC-3919). Standalone has no consumer beyond the Routing mixin — the
 * original error still propagates at the call site.
 */
export function failed(error: unknown) {
    readyState.reject(error);
}

// After a dispose/re-init cycle (embedded mount/unmount, PTC-3919) consumers
// must wait for the *new* app's ready() — live bindings hand them the fresh
// promise.
onAppDispose(() => {
    readyState = makeReadyState();
    isReady = readyState.promise;
});

export const Routing = <T extends Constructor<LitElement>>(baseElement: T) => {
    class M extends baseElement {
        router = router;

        get routeTitle(): string | undefined {
            return undefined;
        }

        get helpPage(): string | undefined {
            return undefined;
        }

        routeProperties: RoutePropertyDeclaration[];

        @property({ type: Boolean, reflect: true })
        active: boolean = false;

        protected readonly routePattern: RegExp = /$^/;

        private _routeHandler = () => this.routeChanged(router.path, router.params);
        private _beforeRouteChangedHandler = (e: Event) => this.beforeRouteChanged(e);
        private _beforeUnloadHandler = (e: Event) => this.beforeUnload(e);

        private _visibilityChange = () => {
            if (document.visibilityState === "visible") {
                this.pageFocused();
            }
        };

        protected defaultRange?: () => DateRange;
        protected rangeChanged?: (range: DateRange) => void;

        @routeProperty({ param: "date" })
        date: DateString;

        @routeProperty({ param: "from" })
        dateFrom: DateString;

        @routeProperty({ param: "to" })
        dateTo: DateString;

        @routeProperty({ type: Number, param: "venue" })
        venueId: number;

        @routeProperty({ param: "entity-filters" })
        entityFiltersJSON: string;

        get dateRange(): DateRange | null {
            return this.dateFrom && this.dateTo
                ? {
                      from: this.dateFrom,
                      to: this.dateTo,
                  }
                : null;
        }

        get venue() {
            return app.getVenue(this.venueId);
        }

        get year() {
            return this.date && parseDateString(this.date)!.getFullYear();
        }

        shouldUpdate(changes: Map<string, unknown>) {
            return changes.has("active") || this.active;
        }

        updated(changes: Map<string, unknown>): void {
            if ((changes.has("dateFrom") || changes.has("dateTo")) && this.dateRange && this.rangeChanged) {
                this.rangeChanged(this.dateRange);
            }
            if (changes.has("entityFiltersJSON") && this.entityFiltersJSON) {
                try {
                    const employeeFilters = JSON.parse(this.entityFiltersJSON) as EntityFilter[];
                    if (Array.isArray(employeeFilters) && employeeFilters.length > 0) {
                        app.updateEmployeeFilters({ filters: employeeFilters });
                    }
                } catch (e) {
                    console.error("Error parsing entityFiltersJSON:", e);
                }
            }
        }

        get hasChanges(): boolean {
            return false;
        }

        clearChanges(): void {}

        connectedCallback() {
            super.connectedCallback();

            document.addEventListener("visibilitychange", this._visibilityChange);
            router.addEventListener("route-changed", this._routeHandler);
            router.addEventListener("before-route-changed", this._beforeRouteChangedHandler);
            window.addEventListener("beforeunload", this._beforeUnloadHandler);

            void (async () => {
                try {
                    await isReady;
                } catch {
                    // Boot failed (see failed()) — there is no initial route
                    // to apply; the failure is reported at the boot site.
                    return;
                }
                this._routeHandler();
            })();
        }

        disconnectedCallback() {
            super.disconnectedCallback();
            router.removeEventListener("route-changed", this._routeHandler);
            router.removeEventListener("before-route-changed", this._beforeRouteChangedHandler);
            window.removeEventListener("beforeunload", this._beforeUnloadHandler);
            document.removeEventListener("visibilitychange", this._visibilityChange);
        }

        go(
            path: string | null,
            params: { [param: string]: string | number | Date | undefined } = {},
            replace?: boolean
        ) {
            for (const [prop, value] of Object.entries(params)) {
                if (typeof value === "undefined") {
                    delete params[prop];
                } else if (typeof value === "number") {
                    params[prop] = value.toString();
                } else if (value instanceof Date) {
                    params[prop] = toDateString(value);
                }
            }

            // If path is null, preserve current path including any hash
            const targetPath = path !== null ? path : router.path + router.hash;
            router.go(targetPath, params as { [param: string]: string }, replace);
        }

        redirect(path: string) {
            this.go(path, undefined, true);
        }

        protected matchRoute(path: string, params: { [param: string]: string }): null | RouteArgs {
            const match = path.match(this.routePattern);

            if (!match) {
                this.active = false;
                return null;
            }

            this.active = true;

            const groups = match.groups;
            const args = match.slice(1) as RouteArgs;
            Object.assign(args, groups);

            for (const prop of this.routeProperties || []) {
                let value = prop.arg ? args[prop.arg] : prop.param ? params[prop.param] : null;
                if (typeof value === "string") {
                    if (prop.parse) {
                        value = prop.parse(value);
                    }
                    this[prop.name as keyof typeof this] = (prop.type || String)(value);
                } else {
                    this[prop.name as keyof typeof this] = value as any;
                }
            }

            if (this.helpPage) {
                setSuggestedHelpPage(this.helpPage);
            }

            return args;
        }

        protected routeChanged(path: string, params: { [prop: string]: string }): void {
            const args = this.matchRoute(path, params);

            if (args) {
                if (this.setDefaults()) {
                    return this.routeChanged(path, this.router.params);
                }

                this.handleRoute(args, params, path);

                if (this.routeTitle) {
                    // The tab title belongs to the host page when embedded.
                    if (appMode !== "embedded") {
                        document.title = `${this.routeTitle} | Pentacode`;
                    }
                }

                // Try to scroll to hash after route is handled and DOM updates
                if (this.router.hash) {
                    // Wait for LitElement to complete its update cycle
                    this.router.scrollToHash(this.router.hash);
                }
            }
        }

        protected beforeRouteChanged(e: Event) {
            if (this.active && this.hasChanges) {
                if (
                    confirm(
                        "Sind Sie sicher, dass Sie diese Seite verlassen wollen? Eventuelle Änderungen werden verworfen."
                    )
                ) {
                    this.clearChanges();
                } else {
                    e.preventDefault();
                }
            }
        }

        protected beforeUnload(e: Event) {
            if (this.active && this.hasChanges) {
                e.preventDefault();
                e.returnValue = false;
            }
        }

        protected setDefaults() {
            const defaults: Record<string, any> = {};
            for (const prop of this.routeProperties || []) {
                if (prop.param && typeof this[prop.name as keyof typeof this] === "undefined" && prop.default) {
                    defaults[prop.param] = prop.default();
                }
            }

            if (this.defaultRange && !this.dateRange) {
                const range = this.defaultRange();
                defaults.from = range.from;
                defaults.to = range.to;
            }

            if (Object.entries(defaults).length) {
                router.params = { ...router.params, ...defaults };
                return true;
            }

            return false;
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        protected handleRoute(_args: RouteArgs, _params: { [prop: string]: string }, _path: string) {}

        protected pageFocused() {}
    }

    return M;
};
